import { Collapse, Divider, Grid, ListItem, Paper } from "@mui/material";
import { useState } from "react";
import { useMe } from "../api";
import { ShiftDayList } from "./ShiftDayList";
import { ShiftDayType } from "./ShiftDayType";

const parseShiftGroups = (shifts) =>
  shifts.reduce(
    (accumulator, shift) => {
      const { buckets, groups } = accumulator;
      const {
        contractLocationId,
        contractLocationName,
        dayOfYear,
        displayDate,
        distance,
        endTime,
        jobTitle,
        startDate,
        startTime,
        yearWeek,
      } = shift;

      if (!buckets.hasOwnProperty(contractLocationId)) {
        buckets[contractLocationId] = {
          contractLocationName,
          data: {},
          distance,
        };
      }

      const contractLocationIdData = buckets[contractLocationId].data;

      if (!contractLocationIdData.hasOwnProperty(jobTitle)) {
        contractLocationIdData[jobTitle] = { data: {}, jobTitle };
      }

      const jobTitleData = contractLocationIdData[jobTitle].data;

      if (!jobTitleData.hasOwnProperty(startTime)) {
        jobTitleData[startTime] = { data: {}, startTime };
      }

      const startTimeData = jobTitleData[startTime].data;

      if (!startTimeData.hasOwnProperty(endTime)) {
        startTimeData[endTime] = { data: [], endTime };
        groups.push({
          contractLocationId,
          contractLocationName,
          displayDate,
          distance,
          endTime,
          jobTitle,
          groupKey: `${contractLocationId}:${jobTitle}:${dayOfYear}:${startTime}:${endTime}`,
          startDate,
          startTime,
          shifts: startTimeData[endTime].data,
          yearWeek,
        });
      }

      const endTimeData = startTimeData[endTime].data;

      endTimeData.push(shift);

      return accumulator;
    },
    { buckets: {}, groups: [] },
  );

const generateShiftGroups = (scheduleTypes) => {
  const shiftGroupEntries = Object.entries(scheduleTypes).map(
    ([scheduleType, shifts]) => {
      const { scheduled, unassigned } = shifts;
      const scheduledGroups = parseShiftGroups(scheduled);
      const unassignedGroups = parseShiftGroups(unassigned);

      return [
        scheduleType,
        {
          scheduled: scheduledGroups.groups,
          unassigned: unassignedGroups.groups,
        },
      ];
    },
  );

  return Object.fromEntries(shiftGroupEntries);
};

const ShiftDay = ({ cases = [], shifts, weekday, showUpcoming }) => {
  const { date, dayOfMonth, month, scheduleTypes } = shifts;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedScheduleType, setSelectedScheduleType] = useState();
  const shiftGroups = generateShiftGroups(scheduleTypes);
  const { data: me } = useMe();

  const hasVisibleAddShift = Object.values(shiftGroups).some(
    ({ unassigned = [], scheduled = [] }) => {
      const isScheduled = scheduled.length > 0;
      const isAutoassignable = unassigned
        .flatMap(({ shifts }) => shifts)
        .some(({ isAutoassignable }) => isAutoassignable);

      return !isScheduled && isAutoassignable && me?.canAutoSchedule;
    },
  );

  if (showUpcoming && !hasVisibleAddShift) {
    return null;
  }

  return (
    <div className="m-[10px]">
      <Paper elevation={3}>
        <ListItem className="!p-0">
          <div className="m-0 w-[80px] bg-[var(--color-blue)] py-[10px] text-center text-white">
            {weekday}
            <br />
            {dayOfMonth}
            <br />
            {month}
          </div>

          <div className="flex w-full flex-row items-center justify-around px-[10px]">
            <Grid container>
              <Grid item xs={4} className="!flex !items-center !justify-center">
                <ShiftDayType
                  cases={cases.filter((c) => c.shiftType === "Day")}
                  shiftGroups={shiftGroups["Day"]}
                  scheduleType={"Day"}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  selectedScheduleType={selectedScheduleType}
                  setSelectedScheduleType={setSelectedScheduleType}
                  date={date}
                  showUpcoming={showUpcoming}
                />
              </Grid>

              <Divider
                orientation="vertical"
                flexItem
                className="!mr-[-1px] !h-[82px]"
              />

              <Grid item xs={4} className="!flex !items-center !justify-center">
                <ShiftDayType
                  cases={cases.filter((c) => c.shiftType === "Swing")}
                  shiftGroups={shiftGroups["Swing"]}
                  scheduleType={"Swing"}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  selectedScheduleType={selectedScheduleType}
                  setSelectedScheduleType={setSelectedScheduleType}
                  date={date}
                  showUpcoming={showUpcoming}
                />
              </Grid>

              <Divider
                orientation="vertical"
                flexItem
                className="!mr-[-1px] !h-[82px]"
              />

              <Grid item xs={4} className="!flex !items-center !justify-center">
                <ShiftDayType
                  cases={cases.filter((c) => c.shiftType === "Grave")}
                  shiftGroups={shiftGroups["Grave"]}
                  scheduleType={"Grave"}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  selectedScheduleType={selectedScheduleType}
                  setSelectedScheduleType={setSelectedScheduleType}
                  date={date}
                  showUpcoming={showUpcoming}
                />
              </Grid>
            </Grid>
          </div>
        </ListItem>
      </Paper>

      <Collapse in={isOpen} unmountOnExit>
        <Paper elevation={3} className="mt-1">
          {selectedScheduleType && (
            <ShiftDayList
              shiftGroup={shiftGroups[selectedScheduleType]?.["unassigned"]}
              setIsOpen={setIsOpen}
              weekday={weekday}
              dayOfMonth={dayOfMonth}
              month={month}
              selectedScheduleType={selectedScheduleType}
              date={date}
            />
          )}
        </Paper>
      </Collapse>
    </div>
  );
};

export { ShiftDay };
